import React, {useEffect, useState} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../context/userContext.js';
import { createCheckoutSession, accessPortal, getActivePaymentMethod, renewSub, getUser, deleteAccount, changePassword, changeUsername } from '../../api/axios.js';
import BaseLayout from '../../components/BaseLayout.js';
import Card from '../../components/Card.js';
import SettingTabs from '../../components/SettingsTab.js';
import SubscriptionPlanCard from '../../components/SubscriptionPlanCard.js';
import PaymentMethodCard from '../../components/PaymentMethodCard.js';
import ShortButton from '../../components/ShortButton.js';
import { CookingPot } from '../../components/CookingSoon.js';
import { Typography } from '@mui/material';
import Cookies from 'js-cookie';
import { motion, AnimatePresence } from 'framer-motion';

const Settings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, contextLogout, refreshSubscription } = useUser();
  
  const [activeTab, setActiveTab] = useState('billing');
  const [isFirstTime, setFirstTime] = useState(true);
  const [isPremiumSub, setPremiumSub] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Account settings state
  const [userData, setUserData] = useState(null);
  const [username, setUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isGoogleUser, setIsGoogleUser] = useState(false);

  const tabs = [
        { label: 'Profile', value: 'profile' },
        // { label: 'Platform', value: 'platform' },
        { label: 'Billing', value: 'billing' }
      ];

      const contentVariants = {
        enter: {
          x: 20,
          opacity: 0,
        },
        center: {
          x: 0,
          opacity: 1,
          transition: {
            type: "spring",
            stiffness: 300,
            damping: 24,
            duration: 0.5
          }
        },
        exit: {
          x: -20,
          opacity: 0,
          transition: {
            duration: 0.3
          }
        }
      };

      const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            when: "beforeChildren",
            staggerChildren: 0.1,
            duration: 0.4
          }
        }
      };
      
      const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
          y: 0,
          opacity: 1,
          transition: {
            type: "spring",
            stiffness: 300,
            damping: 24
          }
        }
      };

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      try {
      await refreshSubscription();
      
      if (user.data.planType === "Premium") {
        setPremiumSub(true);
      } else {
        setPremiumSub(false);
      }

      if (user.data.stripeCustomerId) {
        setFirstTime(false);
      } else {
        setFirstTime(true);
      }} catch (error) {
        setError('Failed to load subscription information');
      }
    };

    const checkPaymentMethod = async () => {
      try {
        const response = await getActivePaymentMethod();
        setPaymentMethod(response.data.activePaymentMethod);
      } catch (error) {
        setError('Failed to load active payment method')
      }
    };

    const getUserData = async () => {
      try {
        const response = await getUser();
        if (response.data) {
            setUserData(response.data);
            setUsername(response.data.username);
            if (response.data.googleId) {
                setIsGoogleUser(true);
            }
          }
      } catch (error) {
          setError("Error fetching account details");
      }
    };
    const loadData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          checkSubscriptionStatus(),
          checkPaymentMethod(),
          getUserData()
        ]);
      } catch (error) {
        setError('Error loading page data');
      } finally {
        // Add slight delay for smooth transition
        setTimeout(() => setIsLoading(false), 300);
      }
    };
    
    checkSubscriptionStatus();
    checkPaymentMethod();
    getUserData();
    loadData();
  }, [location.pathname, user, refreshSubscription]);

  const handleUsernameChange = (e) => setUsername(e.target.value);
  const handleCurrentPasswordChange = (e) => setCurrentPassword(e.target.value);
  const handleNewPasswordChange = (e) => setNewPassword(e.target.value);
  const handleConfirmNewPasswordChange = (e) => setConfirmNewPassword(e.target.value);

  const handleSaveChanges = async () => {
    const confirmed = window.confirm('Are you sure you want to save these changes?');
    if (!confirmed) return;

    try {
        if (userData.username !== username) {
            await changeUsername(username);
            alert(`Username updated successfully to ${username}!`);
        }

        if (!isGoogleUser && newPassword && newPassword === confirmNewPassword) {
            await changePassword(currentPassword, newPassword);
            alert("Password updated successfully!");
        } else if (newPassword !== confirmNewPassword) {
            alert("New password and confirmation do not match.");
        }
    } catch (error) {
        console.error("Error updating user information:", error);
        alert("Error updating information. Please try again.");
    }
  };

  const handleDeleteAccount = async () => {
      const confirmed = window.confirm('Are you sure you want to delete your account? This action cannot be undone.');
      if (!confirmed) return;

      try {
          await deleteAccount();
          alert("Account deleted successfully.");
          contextLogout();
          Cookies.remove('token');
          navigate('/', {replace: true});
      } catch (error) {
          console.error("Error deleting account:", error);
          alert("Error deleting account. Please try again.");
      }
  };

  const handleResetData = async () => {
      const confirmed = window.confirm('Are you sure you want to reset your submission data?');
      if (!confirmed) return;

      try {
          console.log("Resetting submission data");
      } catch (error) {
          console.error("Error resetting data:", error);
      }
  };

  // Handle checkout button click
  const handleCheckout = async () => {
    try {
      setError(null);
      
      // Call your backend to create a checkout session
      const response = await createCheckoutSession();

      // Check if response contains the URL and redirect
      if (response.data && response.data.url) {
        window.location.href = response.data.url;
      } else {
        setError('No checkout URL returned from the server');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError('Failed to initiate checkout. Please try again.');
    } finally {
      // setLoading(false);
    }
  };

  const handleManageBilling = async () => {
      try {
        setError(null);
        
        const response = await accessPortal();
        
        if (response.data && response.data.url) {
          window.location.href = response.data.url;
        } else {
          setError('No portal URL returned');
        }
      } catch (error) {
        setError('Could not access billing portal. Please try again later.');
      } finally {
        // setLoading(false);
      }
    };

  const handleRenewSub = async () => {
    try {
      setError(null);
      
      await renewSub();
      handleManageBilling();
    } catch (error) {
      setError('Could not renew subscription. Try again later');
    }
  }
  
  const renderTabContent = () => {
    switch(activeTab) {
        case 'profile':
        return (
        <motion.div
        key="profile"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
      >
        <Card>
          <div className="account-inner">
            <h3 className="section-title">Account Details</h3>
            {!userData ? (
              <Typography variant="h6" sx={{ color: 'white' }}>
                No data found. Please login and try again.
              </Typography>
            ) : (
              <div className="account-form">
                <div className="form-section">
                  <div className="form-row form-row-disabled">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={userData.email}
                      disabled={true}
                    />
                  </div>
                  <div className={`form-row ${isGoogleUser ? 'form-row-disabled' : ''}`}>
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      className="form-input"
                      value={username}
                      onChange={handleUsernameChange}
                      disabled={isGoogleUser}
                    />
                  </div>
                </div>
                
                {!isGoogleUser && (
                  <div className="form-section">
                    <h3 className="section-title">Password</h3>
                    
                    <div className="form-row">
                      <label className="form-label">Current Password</label>
                      <input
                        type="password"
                        className="form-input"
                        placeholder="••••••••••••"
                        value={currentPassword}
                        onChange={handleCurrentPasswordChange}
                      />
                    </div>
                    
                    <div className="form-row">
                      <label className="form-label">New Password</label>
                      <input
                        type="password"
                        className="form-input"
                        placeholder="••••••••••••"
                        value={newPassword}
                        onChange={handleNewPasswordChange}
                      />
                    </div>
                    
                    <div className="form-row">
                      <label className="form-label">Confirm Password</label>
                      <input
                        type="password"
                        className="form-input"
                        placeholder="••••••••••••"
                        value={confirmNewPassword}
                        onChange={handleConfirmNewPasswordChange}
                      />
                    </div>
                  </div>
                )}
                
                <div className="button-row">
                  <div className="button-left">
                    <button
                        className="delete-button"
                        onClick={handleDeleteAccount}
                      >
                        Delete Account
                    </button>
                  </div>
                  <div className="button-right">
                    <ShortButton
                      text="Update Details"
                      onClick={handleSaveChanges}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
        </motion.div>
      );
        case 'platform':
        return <Card>Platform content goes here</Card>;
        case 'billing':
        return (
          <motion.div
          key="billing"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          <div>
            {!isFirstTime ? (
              <div className='payment-card'>
                {paymentMethod ? (
                  <PaymentMethodCard cardType={paymentMethod.brand} lastFour={paymentMethod.last4} isActive={true} onClick={handleManageBilling}/>
                ) : (
                  <PaymentMethodCard isDescriptionOnly={true} onClick={handleRenewSub}/>
                )
                }         
              </div> ) : (
                <div className='payment-card'>
                  <PaymentMethodCard isPromptOnly={true}/>
                </div>
              )
            }
            <div className='subscription-plans'>
              <div className='premium-plans'>
                {isPremiumSub ? (
                  <SubscriptionPlanCard isActive={true}/>
                ) : (
                  isFirstTime ? (
                    <SubscriptionPlanCard onClick={handleCheckout}/>
                  ) : (
                    <SubscriptionPlanCard onClick={handleRenewSub}/>
                  )
                )}
              </div>

              <div className='free-plans'>
                {!isPremiumSub ? (
                  <SubscriptionPlanCard isPremium={false} isActive={true}/>
                ) : (
                  <SubscriptionPlanCard isPremium={false}/>
                )}
              </div>
            </div>
          </div>
          </motion.div>
        );
        default:
        return null;
    }
    };

  return (
    <>
        <style>
        {`
            .center-container {
                display: flex;
            }

            .grid-container {
                margin: 0 auto;
                padding: 0 12px;
                display: flex;
                flex-direction: column;
                position: relative;
                padding-top:48px;
            }

            .form-fields {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 16px;
                width: 100%;
            }

            .title-container {
                position: absolute;
                top: 0;
                left: 12px;
                text-align: left;
            }

            .title-gap {
                margin-bottom: 32px;
            }

            .textarea-container {
                width: 100%;
                margin: 4px 0;
            }

            .field-label {
            font-family: 'Poppins', sans-serif;
            font-size: 14px;
            color: #E1E1E6;
            }

            .field-input {
            padding: 12px;
            border-radius: 4px;
            border: 1px solid #6B3A8F;
            background: rgba(26, 26, 26, 0.5);
            color: #E1E1E6;
            font-family: 'Poppins', sans-serif;
            width: 100%;
            }

            textarea.field-input {
            min-height: 80px;
            resize: vertical;
            }

            .main-title {
                text-align: left;
                padding: 0 12px;
            }

            .error-message {
                padding-top: 16px;
                text-align: center;
                color: red;
            }

            .subscription-plans {
              display: flex;
              gap: 4%;
              justify-content: left;
            }

            .premium-plans {
              padding: 8px; /* This creates space for the border */
              border-radius: 24px; /* Slightly larger than inner container */
              background: linear-gradient(55deg,rgb(224, 86, 185),rgb(75, 31, 114));
              margin-bottom: 5%;
            }

            .free-plans {
              padding: 8px; /* This creates space for the border */
              border-radius: 24px; /* Slightly larger than inner container */
              margin-bottom: 5%;
            }
            
            .payment-card{ 
              max-width: 80%;
              margin-bottom: 5%;
            }

            .account-inner {
              display: flex;
              flex-direction: column;
              width: 100%;
            }
            
            .account-form {
              display: flex;
              flex-direction: column;
              width: 100%;
            }
            
            .form-section {
              margin-bottom: 5%;
            }
            
            .form-row {
              display: flex;
              margin-bottom: 6%;
              align-items: center;
            }
            
            .form-label {
              width: 200px;
              font-size: 16px;
              color: #E1E1E6;
              text-align: left;
            }
            
            .form-input {
              padding: 12px;
              border-radius: 8px;
              border: 1px solid #6B3A8F;
              background: rgba(26, 26, 26, 0.5);
              color: #E1E1E6;
              font-family: 'Poppins', sans-serif;
              width: 100%;
            }

            .form-row-disabled {
              position: relative;
            }

            .form-row-disabled::after {
              content: "🔒";
              position: absolute;
              right: 16px;
              top: 50%;
              transform: translateY(-50%);
              opacity: 0.7;
            }
            
            .section-title {
              color: #9797FA;
              font-size: 18px;
              font-weight: 600;
              margin-bottom: 24px;
            }

            .button-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              width: 100%;
            }
            
            .button-left {
              display: flex;
              justify-content: flex-start;
            }
            
            .button-right {
              display: flex;
              justify-content: flex-end;
            }
            
            .form-actions {
              display: flex;
              justify-content: flex-end;
              margin-top: 20px;
            }
            
            .delete-account {
              margin-top: 40px;
              text-align: center;
            }
            
            .delete-button {
              background: transparent;
              border: 1px solid #dc2626;
              color: #dc2626;
              padding: 24px 20px;
              border-radius: 24px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 600;
              transition: all 0.3s ease;
            }
            
            .delete-button:hover {
              background: rgba(220, 38, 38, 0.1);
            }

            .loading-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                padding: 24px;
                text-align: center;
            }

            p {
                font-family: 'Poppins', sans-serif;
                font-size: 14px;
                color: #E1E1E6;
                margin: 8px 0 0 0;
                opacity: 0.8;
            }
        
        .fade-in {
          animation: fadeIn 0.5s ease-in;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        `}
        </style>

        <BaseLayout>
      {isLoading ? (
        <div className="loading-container">
          <CookingPot />
          <Typography variant="body2">Loading...</Typography>
        </div>    
      ) : (
        <motion.div 
          className="center-container fade-in"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="grid-container">
            <motion.div variants={itemVariants}>
              <SettingTabs 
                activeTab={activeTab} 
                setActiveTab={setActiveTab} 
                tabs={tabs}
              />
            </motion.div>
            
            <motion.div variants={itemVariants} className="form-container" style={{ transform: 'translateZ(0)' }}>
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={activeTab}
                  variants={contentVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                >
                  {renderTabContent()}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </div>
        </motion.div>
      )}
    </BaseLayout>
  </>
);
}

export default Settings;